import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { Icon, Dropdown, Input } from 'semantic-ui-react'
import type { DropdownProps } from 'semantic-ui-react'
import { invert } from 'lodash'

import { useAppSelector } from 'redux/hooks'

import { AUTH_SORTS, AUTH_STACKS, HUMAN_SORT, HUMAN_STACK, SORT, STACK } from 'types/deck'

import { humanReadableObjectToDropdown } from 'utils/DropdownConversions'
import { generateDeckUrl } from 'utils/deckSeo'

import PhatButton from 'components/formElements/PhatButton'
import Link from 'components/elements/Link'
import Modal from 'components/elements/Modal'
import QRViewer from 'components/deckPage/header/Share/QRViewer'
import { Button as TriggerButton } from 'components/deckPage/header/BannerButton'

import { Container, OptionsContainer } from './style'

import styles from './share.module.scss'

type Props = {}

const Share = (_props: Props) => {
  const [shareModalOpen, setShareModalOpen] = useState(false)

  return (
    <>
      <TriggerButton noTextOnSmall icon="share alternate" label="Share" onClick={() => setShareModalOpen(true)} />

      <Modal open={shareModalOpen} closeButton onClose={() => setShareModalOpen(false)} modalClassName={styles.modal}>
        <ShareContent />
      </Modal>
    </>
  )
}

export default Share

export const ShareContent = () => {
  const router = useRouter()

  const currentSort = useAppSelector(state => state.deck.sorting)
  const currentStack = useAppSelector(state => state.deck.stack)
  const deckId = useAppSelector(state => state.deck.id)
  const deckName = useAppSelector(state => state.deck.name)

  const [copied, setCopied] = useState(false)
  const [viewAsQR, setViewAsQR] = useState(false)

  // options
  const [sort, setSort] = useState<any>()
  const [stack, setStack] = useState<any>()

  const generateUrl = () => {
    const deckUrl = generateDeckUrl(deckId, deckName)
    let url = `${window.origin}${deckUrl}`

    const humanSort = invert(SORT)[sort]
    const humanStack = invert(STACK)[stack]

    if (humanSort || humanStack) url += '?'
    if (humanSort) url += `sort=${humanSort.toLowerCase()}&`
    if (humanStack) url += `stack=${humanStack.toLowerCase()}`

    return url
  }

  const generatedUrl = generateUrl()

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedUrl)

    setCopied(true)

    setTimeout(() => setCopied(false), 3000) // 3 seconds
  }

  const handleUseMySettings = () => {
    setSort(currentSort)
    setStack(currentStack)
  }

  return (
    <>
      <h3>Share Menu</h3>
      <div>
        {viewAsQR ? (
          <QRViewer link={generatedUrl} onCloseQrViewer={() => setViewAsQR(false)} />
        ) : (
          <Container style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            <Input
              size="large"
              iconPosition="left"
              labelPosition="right"
              action={{
                color: copied ? 'green' : 'blue',
                onClick: handleCopy,
                content: copied ? 'Copied' : 'Copy',
              }}
              icon="linkify"
              value={generatedUrl}
            />

            <div className={styles.row}>
              <PhatButton icon="qrcode" onClick={() => setViewAsQR(true)}>
                View link as QR Code
              </PhatButton>
              <Link href={`/decks/image/${deckId}`} about="_blank">
                <PhatButton icon="image outline">Generate deck image</PhatButton>
              </Link>
            </div>

            <h3>Link Options</h3>
            <p>
              To specify how others see your deck, select custom sorting and stacking options. This will change the link
              generated above.
            </p>

            <OptionsContainer>
              <PhatButton onClick={handleUseMySettings}>Apply Current Settings</PhatButton>
            </OptionsContainer>

            <label>
              Sorting Options <Icon name="sort amount down" />
            </label>
            <Dropdown
              selection
              clearable
              value={sort}
              placeholder="Alphabetical, CMC, Colors, etc"
              options={humanReadableObjectToDropdown(HUMAN_SORT).filter(
                (sort: any) => !AUTH_SORTS.includes(sort.value),
              )}
              onChange={(_e: React.SyntheticEvent, { value }: DropdownProps) => setSort(value)}
            />

            <label>
              Stacking Options <Icon name="clone outline" />
            </label>
            <Dropdown
              selection
              clearable
              placeholder="Types, Custom, Multiple, Colors, etc"
              value={stack}
              options={humanReadableObjectToDropdown(HUMAN_STACK).filter(
                (stack: any) => !AUTH_STACKS.includes(stack.value),
              )}
              onChange={(_e: React.SyntheticEvent, { value }: DropdownProps) => setStack(value)}
            />
          </Container>
        )}
      </div>
    </>
  )
}
